/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useContext, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { MfaLoginProps } from '../../api/mfa/types';
import Snackbar from '../../components/Snackbar';
import { Constants } from '../../constants/mfa';
import { GlobalContext } from '../../context/global';
import {
  getMfaPendingSession,
  removeMfaPendingSession,
  setLocalStorageToken,
  setMfaPendingSession,
} from '../../helpers/localStorage';
import { resolveMfaMode } from '../../helpers/mfa';
import useSnackbar from '../../hooks/useSnackbar';
import { SetupTotp } from './SetupTotp';
import {
  ContainerGrid,
  ContentBox,
  ContentGrid,
  LogoGrid,
  LogoImg,
} from './styles';
import { VerifyCode } from './VerifyCode';

/**
 * Completes the second factor of a login that came back MFA-pending. The token
 * it works with is the pending one, kept outside the session storage: until it
 * is exchanged for a full one here, no other screen may run (REAL-3264).
 */
export function Mfa({ logo }: { logo: string }): JSX.Element {
  const [session, setSession] = useState(getMfaPendingSession);

  const { openSnackbar } = useContext(GlobalContext);
  const { handleSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const handleExpired = useCallback((): void => {
    removeMfaPendingSession();
    handleSnackbar(Constants.expiredSession, true);
    navigate('/login', { replace: true });
  }, [navigate]);

  const handleAuthenticated = useCallback(
    (payload: MfaLoginProps): void => {
      if (!payload.access_token) {
        handleSnackbar(Constants.genericError, true);
        return;
      }

      // A verified factor does not always complete the session: an enforced
      // user who verified OTP comes back still pending, and the setup is the
      // only way out. Trust the token, not the endpoint that was called.
      const nextMode = resolveMfaMode(payload);
      if (nextMode) {
        const nextSession = { token: payload.access_token, mode: nextMode };
        setMfaPendingSession(nextSession);
        setSession(nextSession);
        return;
      }

      setLocalStorageToken(payload.access_token);
      removeMfaPendingSession();
      // Mounts the Oversight token refresher without a reload, as the login does.
      window.dispatchEvent(new Event('token-set'));
      navigate('/home', { replace: true });
    },
    [navigate]
  );

  const handleError = useCallback(
    (message: string): void => {
      // The pending token lasts 30 min; past that every MFA call answers 401.
      if (message === Constants.unauthenticated) {
        handleExpired();
        return;
      }
      handleSnackbar(message, true);
    },
    [handleExpired]
  );

  const handleCancel = useCallback((): void => {
    // No /logout call: that endpoint rejects a pending token with 401.
    removeMfaPendingSession();
    navigate('/login', { replace: true });
  }, [navigate]);

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  return (
    <ContainerGrid container>
      <LogoGrid item xs={6.5} xl={7.5}>
        <LogoImg>
          <img src={logo} alt="Logo" />
        </LogoImg>
      </LogoGrid>
      <ContentGrid item xs={5.5} xl={4.5}>
        <ContentBox>
          {session.mode === 'setup' ? (
            <SetupTotp
              token={session.token}
              onAuthenticated={handleAuthenticated}
              onCancel={handleCancel}
              onError={handleError}
            />
          ) : (
            <VerifyCode
              mode={session.mode}
              token={session.token}
              onAuthenticated={handleAuthenticated}
              onCancel={handleCancel}
              onError={handleError}
            />
          )}
        </ContentBox>
      </ContentGrid>
      {openSnackbar && <Snackbar />}
    </ContainerGrid>
  );
}
